import type { Editor } from '@tiptap/react';

import { Box, Button, Flex } from '@chakra-ui/react';
import { useEditorState } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { useMemo } from 'react';

import {
  createMarkdownTableCommands,
  isMarkdownTableActive,
} from './markdown-table-menu';

interface MarkdownTableMenuPopoverProps {
  editor: Editor | null;
}

const MARKDOWN_TABLE_MENU_BUTTON_SIZE = 44;
const MARKDOWN_TABLE_MENU_BUTTON_GAP = 8;
const MARKDOWN_TABLE_MENU_PADDING = 16;

export function MarkdownTableMenuPopover({
  editor,
}: MarkdownTableMenuPopoverProps) {
  useEditorState({
    editor,
    selector: ({ editor: currentEditor }) =>
      currentEditor ? isMarkdownTableActive(currentEditor) : false,
  });

  const commands = useMemo(
    () => (editor ? createMarkdownTableCommands(editor) : []),
    [editor],
  );

  const menuWidth =
    commands.length * MARKDOWN_TABLE_MENU_BUTTON_SIZE +
    Math.max(0, commands.length - 1) * MARKDOWN_TABLE_MENU_BUTTON_GAP +
    MARKDOWN_TABLE_MENU_PADDING;

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={() => editor.isEditable && isMarkdownTableActive(editor)}
      options={{
        placement: 'top',
        offset: 8,
      }}
    >
      <Box
        className="markdown-editor-table-menu"
        role="toolbar"
        aria-label="Table toolbar"
        minW={`${menuWidth}px`}
      >
        <Flex className="markdown-editor-table-menu__toolbar" wrap="wrap">
          {commands.map((command) => {
            const Icon = command.icon;
            const disabled = !command.canRun();

            return (
              <Button
                key={command.id}
                className="markdown-editor-table-menu__button"
                variant="ghost"
                w="11"
                h="11"
                p="0"
                justifyContent="center"
                alignItems="center"
                fontWeight="normal"
                title={command.label}
                aria-label={command.label}
                disabled={disabled}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={command.run}
              >
                <Icon aria-hidden size={18} />
              </Button>
            );
          })}
        </Flex>
      </Box>
    </BubbleMenu>
  );
}
